import { getKidSub } from '@/auth/authStore';

import { loadPdfJs } from './pdfJsLoader';

/**
 * Page 1 of a PDF material, drawn small for the material list thumbnail with the lazy pdf.js
 * loader. One draw per learner and document; the page is never uploaded.
 */

/** Long edge of a drawn PDF page: twice the 120 px card box, for sharp high-density screens. */
export const PDF_THUMBNAIL_EDGE_PX = 240;

const pdfThumbnails = new Map<string, Promise<string>>();

/** Clears drawn PDF pages (tests). Keys carry the learner id, so siblings never share one. */
export function clearPdfThumbnails(): void {
  pdfThumbnails.clear();
}

async function drawPdfFirstPage(url: string): Promise<string> {
  const { getDocument } = await loadPdfJs();
  const pdf = await getDocument({ url }).promise;
  try {
    const page = await pdf.getPage(1);
    const base = page.getViewport({ scale: 1 });
    const longEdge = Math.max(base.width, base.height);
    if (!(longEdge > 0)) throw new Error('PDF page has no size');
    const viewport = page.getViewport({ scale: PDF_THUMBNAIL_EDGE_PX / longEdge });
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.floor(viewport.width));
    canvas.height = Math.max(1, Math.floor(viewport.height));
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas is not available');
    context.fillStyle = 'white';
    context.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvas, canvasContext: context, viewport }).promise;
    return canvas.toDataURL('image/png');
  } finally {
    void pdf.destroy();
  }
}

/**
 * Page 1 of a PDF as a data URL, cached per learner and document. A failed draw is not cached,
 * so the next visit can try again.
 */
export function pdfThumbnail(documentId: string, url: string): Promise<string> {
  const key = `${getKidSub() ?? 'anonymous'}:${documentId}`;
  const cached = pdfThumbnails.get(key);
  if (cached) return cached;
  const drawing = drawPdfFirstPage(url);
  pdfThumbnails.set(key, drawing);
  drawing.catch(() => pdfThumbnails.delete(key));
  return drawing;
}
